import { completeWixConnect } from './wixConnect';

export const wixLocal = process.env.REACT_APP_WIX_LOCAL_PROFILE === 'isolated';
export const wixMultiEnabled = process.env.REACT_APP_WIX_MULTI_APP_ENABLED === 'true';
const base = () => process.env.REACT_APP_API_BASE_URL;
export const wixKey = (app, kind) => `wix-product:${app}:${kind}`;

export function getWixGrant(app) { return sessionStorage.getItem(wixKey(app, 'grant')); }
export function setWixGrant(app, grant) { sessionStorage.setItem(wixKey(app, 'grant'), grant); }
export function wixBrowserKey(app) {
  const key = wixKey(app, 'browser');
  let value = sessionStorage.getItem(key);
  if (!value) {
    value = Array.from(crypto.getRandomValues(new Uint8Array(32)), b => b.toString(16).padStart(2, '0')).join('');
    sessionStorage.setItem(key, value);
  }
  return value;
}

export async function wixRequest(app, path, options = {}) {
  if (!wixMultiEnabled) throw new Error('This app is not available.');
  const root = base();
  if (wixLocal && root !== 'http://127.0.0.1:58139/api/') throw new Error('Unsafe local API target.');
  const token = wixLocal ? sessionStorage.getItem('wix-local-session') : localStorage.getItem('token');
  const response = await fetch(`${root}wix/apps/${encodeURIComponent(app)}/${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(getWixGrant(app) ? { 'X-Wix-Installation': getWixGrant(app) } : {}), ...options.headers },
  });
  const data = await response.json();
  if (!response.ok) {
    const error = new Error(typeof data.detail === 'string' ? data.detail : 'The request could not be completed.');
    error.status = response.status;
    throw error;
  }
  return data;
}

// StrictMode may run an entry effect twice. Coalesce only in-flight handshakes.
const handshakes = new Map();
export function wixHandshake(app, search) {
  const key = app + search;
  if (handshakes.has(key)) return handshakes.get(key);
  const promise = (async () => {
    const params = new URLSearchParams(search);
    for (const key of ['instance', 'signedInstance', 'code', 'token', 'state', 'instanceId', 'appId', 'tenantId']) {
      if (params.getAll(key).length > 1) throw new Error('Ambiguous Wix installation parameters.');
    }
    if ((params.has('instance') && params.has('signedInstance')) ||
        ([params.has('instance') || params.has('signedInstance'), params.has('code'), params.has('token')].filter(Boolean).length > 1)) {
      throw new Error('Conflicting Wix installation parameters.');
    }
    if (params.has('signedInstance') && !params.get('instanceId')) throw new Error('Missing Wix installation identity.');
    if ((params.get('state') || '').startsWith('pc_')) return completeWixConnect(params);
    if (!search) return null;
    const config = await wixRequest(app, 'config');
    if (config.auth_mode === 'easy_oauth' && (params.has('code') || params.has('token') || params.has('state'))) {
      throw new Error('Reopen this app from your Wix dashboard to continue.');
    }
    const instance = params.get('instance') || params.get('signedInstance');
    if (instance) return wixRequest(app, 'resolve', {
      method: 'POST', body: JSON.stringify({ instance, instance_id: params.get('instanceId') || undefined,
        app_id: params.get('appId') || undefined, site_id: params.get('tenantId') || undefined }),
    });
    if (params.has('code')) return wixRequest(app, 'callback', {
      method: 'POST', body: JSON.stringify({ code: params.get('code'), state: params.get('state'), browser: wixBrowserKey(app) }),
    });
    if (params.has('token')) return wixRequest(app, 'install', {
      method: 'POST', body: JSON.stringify({ token: params.get('token'), browser: wixBrowserKey(app) }),
    });
    throw new Error('Open this app from your Wix dashboard to continue.');
  })();
  handshakes.set(key, promise);
  // Keep the completed result through React's effect remount, then release it.
  promise.finally(() => setTimeout(() => handshakes.delete(key), 1000)).catch(() => {});
  return promise;
}
